import React from 'react';
import asyncSeries from 'async/series';
import EventEmitter from 'events';

import util from '../tools/util';
import UserStore from './user_store';

const g_orgListMap = new Map<number, any[]>();
const g_deviceMap: Map<number, any> = new Map();
const g_volumeMap: Map<string, any> = new Map();

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init(done) {
  done && done();
}
function isReady() {
  return true;
}
function getList(org_id: number) {
  return g_orgListMap.get(org_id);
}
function getDevice(device_id: number) {
  return g_deviceMap.get(device_id);
}
function getVolume(device_id: number, volume_id: number) {
  const key = `${device_id}-${volume_id}`;
  return g_volumeMap.get(key);
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useList(org_id: number) {
  const _get = React.useCallback(() => getList(org_id), [org_id]);
  return React.useSyncExternalStore(_subscribe, _get);
}
export function useDevice(device_id: number) {
  const _get = React.useCallback(() => {
    return getDevice(device_id);
  }, [device_id]);
  return React.useSyncExternalStore(_subscribe, _get);
}
const fetch = util.herd(
  (org_id: number, done?: (err: any) => void) => {
    const opts = { url: `/api/1/org/${org_id}/device` };
    UserStore.get(opts, (err, body) => {
      if (err) {
        util.errorLog('DeviceStore.fetch: err:', err);
      } else if (body) {
        const old = g_orgListMap.get(org_id);
        if (!util.deepEqual(old, body.device_list)) {
          g_orgListMap.set(org_id, body.device_list);
          _emit('update');
        }
      }
      done?.(err);
    });
  },
  (org_id) => String(org_id)
);
const fetchDevice = util.herd(
  (device_id: number, done?: (err: any) => void) => {
    const org_id = UserStore.getCurrentOrgId();
    const opts = { url: `/api/1/org/${org_id}/device/${device_id}` };
    UserStore.get(opts, (err, body) => {
      if (err) {
        util.errorLog('DeviceStore.fetchDevice: err:', err);
      } else if (body) {
        g_deviceMap.set(device_id, body);
        _emit('device');
      }
      done?.(err);
    });
  },
  (device_id) => String(device_id)
);
function updateDevice(params, done) {
  const { device_id, org_id, ...updates } = params;
  const opts = {
    url: `/api/1/org/${org_id}/device/${device_id}`,
    body: updates,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('DeviceStore.fetchDevice: err:', err);
      err = body?.err || err;
    }
    fetchDevice(device_id, () => done(err));
  });
}
function addVolume(params, done) {
  const { device_id, volume_id } = params;
  const org_id = UserStore.getCurrentOrgId();
  const opts = {
    url: `/api/1/org/${org_id}/device/${device_id}/volume/${volume_id}`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('DeviceStore.addVolume: err:', err, body);
      done(err);
    } else {
      _fetchDeviceOrg({ device_id, org_id }, () => done());
    }
  });
}
function disableVolume(params, done) {
  const { device_id, volume_id } = params;
  const org_id = UserStore.getCurrentOrgId();
  const opts = {
    url: `/api/1/org/${org_id}/device/${device_id}/volume/${volume_id}/disable`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('DeviceStore.disableVolume: err:', err, body);
      done(err);
    } else {
      _fetchDeviceOrg({ device_id, org_id }, () => done());
    }
  });
}
function enableVolume(params, done) {
  const { device_id, volume_id } = params;
  const org_id = UserStore.getCurrentOrgId();
  const opts = {
    url: `/api/1/org/${org_id}/device/${device_id}/volume/${volume_id}/enable`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('DeviceStore.enableVolume: err:', err, body);
      done(err);
    } else {
      _fetchDeviceOrg({ device_id, org_id }, () => done());
    }
  });
}
function getDeviceMediaGrantKey(params, done) {
  const { org_id, device_id, media_grant_id } = params;
  const opts = {
    url: `/api/1/org/${org_id}/device/${device_id}/media_grant/${media_grant_id}/volume_key`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('DeviceStore.getDeviceMediaGrantKey: err:', err, body);
    }
    done(err, body?.volume_encryption_key);
  });
}
function _fetchDeviceOrg(params, done) {
  asyncSeries(
    [
      (done) => fetch(params.org_id, () => done()),
      (done) => fetchDevice(params.device_id, () => done()),
    ],
    done
  );
}
function _editMediaGrant(params: any, done: (err?: any) => void) {
  const { org_id, device_id, suffix, media_grant_id } = params;
  const opts = {
    url: `/web/1/org/${org_id}/device/${device_id}/media_grant${suffix ?? ''}`,
    body: { media_grant_id },
  };
  UserStore.post(opts, (err) => {
    if (err) {
      util.errorLog('DeviceStore._edit: err:', err);
      done?.(err);
    } else {
      _fetchDeviceOrg({ device_id, org_id }, () => done());
    }
  });
}
function addMediaGrant(params: any, done: (err?: any) => void) {
  const { org_id, media_grant_id, device_id } = params;
  _editMediaGrant({ org_id, device_id, media_grant_id }, done);
}
function deleteMediaGrant(params: any, done: (err?: any) => void) {
  const { org_id, media_grant_id, device_id } = params;
  _editMediaGrant(
    { org_id, device_id, media_grant_id, suffix: '/delete' },
    done
  );
}
function enableMediaGrant(params: any, done: (err?: any) => void) {
  const { org_id, media_grant_id, device_id } = params;
  _editMediaGrant(
    { org_id, device_id, media_grant_id, suffix: '/enable' },
    done
  );
}
function disableMediaGrant(params: any, done: (err?: any) => void) {
  const { org_id, media_grant_id, device_id } = params;
  _editMediaGrant(
    { org_id, device_id, media_grant_id, suffix: '/disable' },
    done
  );
}
export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isReady,
  getList,
  getDevice,
  getVolume,
  useList,
  useDevice,
  fetch,
  fetchDevice,
  updateDevice,
  addVolume,
  enableVolume,
  disableVolume,
  getDeviceMediaGrantKey,
  addMediaGrant,
  deleteMediaGrant,
  enableMediaGrant,
  disableMediaGrant,
};
